import { useState } from 'react';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import Button from '@/components/atoms/Button';
import { Label } from '@/components/atoms/label';
import Select from '@/components/atoms/Select';
import { FilterOption } from '@/types';

const baseSortFieldOptions: FilterOption[] = [
  { label: 'Document Date', value: 'date' },
  { label: 'Upload Date', value: 'createdDate' },
  { label: 'Filename', value: 'name' },
  { label: 'Author', value: 'author' },
  { label: 'Type', value: 'type' },
  { label: 'Facts Count', value: 'factsCount' },
];

const sortOrderOptions: FilterOption[] = [
  { label: 'Descending', value: 'DESC' },
  { label: 'Ascending', value: 'ASC' },
];

export const DocSortPopup = ({
  activeSortBy,
  activeSortOrder,
  setSortBy,
  setSortOrder,
  handleClose,
  hasActiveSearch,
}: {
  activeSortBy: string;
  activeSortOrder: 'ASC' | 'DESC';
  setSortBy: (value: string) => void;
  setSortOrder: (value: 'ASC' | 'DESC') => void;
  handleClose: () => void;
  hasActiveSearch?: boolean;
}) => {
  const [tempSortBy, setTempSortBy] = useState<string>(activeSortBy);
  const [tempSortOrder, setTempSortOrder] = useState<'ASC' | 'DESC'>(activeSortOrder);

  // Add chunkMatchCount option when there's an active search
  const sortFieldOptions = hasActiveSearch
    ? [...baseSortFieldOptions, { label: 'Matches in Content', value: 'chunkMatchCount' }]
    : baseSortFieldOptions;

  const handleSubmit = () => {
    trackCustomEvent(TrackingEvents.ApplySortOnDocumentsPage);
    setSortBy(tempSortBy);
    setSortOrder(tempSortOrder);
    handleClose();
  };

  const handleReset = () => {
    setTempSortBy('');
    setTempSortOrder('DESC');
    setSortBy('');
    setSortOrder('DESC');
    handleClose();
  };

  const getCurrentSortField = () => {
    return sortFieldOptions.find((option) => option.value === tempSortBy)?.label || 'No sorting';
  };

  const getCurrentSortOrder = () => {
    return sortOrderOptions.find((option) => option.value === tempSortOrder)?.label || 'Descending';
  };

  return (
    <div className="p-6 text-sm">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Sort Documents</h2>
        <p className="mt-1 text-sm text-gray-500">Choose how to order your documents</p>
      </div>

      <div className="space-y-5">
        {/* Sort Field */}
        <div>
          <Label className="mb-2 block">Sort By</Label>
          <Select
            placeholder="Select sort field"
            options={sortFieldOptions}
            selectedOption={tempSortBy ? sortFieldOptions.find((option) => option.value === tempSortBy) || null : null}
            onChange={(newValue) => {
              if (newValue?.value) {
                setTempSortBy(newValue.value);
              }
            }}
            className="w-full"
          />
          <div className="mt-2 text-xs text-gray-500">
            Currently: <span className="font-medium">{getCurrentSortField()}</span>
          </div>
        </div>

        {/* Sort Order - only show if a sort field is selected */}
        {tempSortBy && (
          <div>
            <Label className="mb-2 block">Order</Label>
            <Select
              placeholder="Select sort order"
              options={sortOrderOptions}
              selectedOption={sortOrderOptions.find((option) => option.value === tempSortOrder) || sortOrderOptions[0]}
              onChange={(newValue) => {
                if (newValue?.value) {
                  setTempSortOrder(newValue.value as 'ASC' | 'DESC');
                }
              }}
              className="w-full"
            />
            <div className="mt-2 text-xs text-gray-500">
              Currently: <span className="font-medium">{getCurrentSortOrder()}</span>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-8 flex gap-3 border-t border-gray-200 pt-4">
        <Button type="delete" rounded="md" text="Clear" onClick={handleReset} className="flex-1" />
        <Button type="primary" rounded="md" text="Apply" onClick={handleSubmit} className="flex-1" />
      </div>
    </div>
  );
};
